/**
 * `tabs:` — the open tabs of every strip (requests, collections, schemas,
 * documents, boards). Enter focuses the tab.
 */
import { PanelTop, FileText, Kanban, Folder, Braces, Send } from 'lucide-react';
import { ORDER } from './order';
import { selectTop } from '../localSearch';
import { openAction } from '../actions';
import { useTabsStore, openDocumentTab, openTaskManagerBoardTab } from '../../store/tabsStore';
import { openPage } from './pages';
import { isApiClientEnabled } from '../../extensions/store';

/** The old palette's tab title rule (unchanged). */
export function tabTitle(tab, t) {
  if (tab.kind === 'collection') return tab.name || t('palette.fallback.collection');
  if (tab.kind === 'schema') return tab.name || t('palette.fallback.schema');
  if (tab.name) return tab.name;
  if (tab.url) {
    try {
      return new URL(tab.url).hostname || t('palette.fallback.newRequest');
    } catch {
      return tab.url.length > 20 ? `${tab.url.substring(0, 20)}...` : tab.url;
    }
  }
  return t('palette.fallback.newRequest');
}

export function tabResults(ctx, state = useTabsStore.getState()) {
  const t = ctx.t;
  const out = [];
  // Request, collection and schema tabs belong to the API Client (#735): not offered while it is off.
  const api = isApiClientEnabled();
  for (const tab of api ? state.requestTabs || [] : []) {
    out.push({ provider: 'tabs', id: `request:${tab.id}`, title: tabTitle({ ...tab, kind: 'request' }, t), subtitle: tab.url || undefined, method: tab.method || 'GET', icon: Send, searchFields: tab.name && tab.url ? [{ text: tab.url, weight: 0.4 }] : [], payload: { tabId: tab.id, tabType: 'request' } });
  }
  for (const tab of api ? state.collectionTabs || [] : []) {
    out.push({ provider: 'tabs', id: `collection:${tab.id}`, title: tabTitle({ ...tab, kind: 'collection' }, t), subtitle: t('palette.kind.collection'), icon: Folder, payload: { tabId: tab.id, tabType: 'collection' } });
  }
  for (const tab of api ? state.schemaTabs || [] : []) {
    out.push({ provider: 'tabs', id: `schema:${tab.id}`, title: tabTitle({ ...tab, kind: 'schema' }, t), subtitle: t('palette.kind.schema'), icon: Braces, payload: { tabId: tab.id, tabType: 'schema' } });
  }
  for (const tab of state.documentTabs || []) {
    out.push({ provider: 'tabs', id: `document:${tab.documentId}`, title: tab.label || t('palette.fallback.untitled'), subtitle: t('palette.kind.document'), icon: FileText, payload: { tabType: 'document', documentId: tab.documentId, label: tab.label, scope: tab.scope } });
  }
  for (const tab of state.taskManagerTabs || []) {
    if (!tab.boardId) continue;
    out.push({ provider: 'tabs', id: `board:${tab.boardId}`, title: tab.label || t('palette.kind.board'), subtitle: t('palette.kind.board'), icon: Kanban, payload: { tabType: 'board', boardId: tab.boardId, label: tab.label } });
  }
  return out;
}

export function focusTab(result, ctx) {
  const p = result.payload || {};
  if (p.tabType === 'document') {
    openDocumentTab({ documentId: p.documentId, label: p.label, scope: p.scope });
    openPage('documents.panel');
  } else if (p.tabType === 'board') {
    openTaskManagerBoardTab({ boardId: p.boardId, label: p.label });
    openPage('task-manager.panel');
  } else {
    ctx.nav.onTabChange?.(p.tabId, p.tabType);
  }
}

export default function create() {
  return {
    id: 'tabs',
    prefixes: ['tabs', 'tab'],
    labelKey: 'palette.scope.tabs',
    icon: PanelTop,
    order: ORDER.tabs,
    kind: 'local-sync',
    available: () => true,
    search(req) {
      return selectTop(tabResults(req.ctx), req);
    },
    actions() {
      return [openAction((r, ctx) => focusTab(r, ctx))];
    },
  };
}
